import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import FormCheckbox from '../../components/form/FormCheckbox';
import { entityNameField } from '../../utils/validation-helper';
import {
  CustomFieldViewModel,
  CreateOrUpdateCustomFieldViewModel,
  CustomFieldDataType,
  CustomFieldType,
  CustomFieldOption,
} from '../../models/configuration';

const DATA_TYPE_OPTIONS = Object.values(CustomFieldDataType).map((v) => ({ label: v, value: v }));

const FIELD_TYPE_OPTIONS = Object.values(CustomFieldType).map((v) => ({ label: v, value: v }));

interface EditCustomFieldProps {
  customField?: CustomFieldViewModel | null;
  onSave: (payload: CreateOrUpdateCustomFieldViewModel) => void;
  onCancel: () => void;
}

interface CustomFieldFormValues {
  name: string;
  dataType: CustomFieldDataType;
  fieldType: CustomFieldType;
  inactive: boolean;
}

const validationSchema = yup.object().shape({
  name: entityNameField('Name').required('Name is required'),
  dataType: yup.string().oneOf(Object.values(CustomFieldDataType)).required('Data type is required'),
  fieldType: yup.string().oneOf(Object.values(CustomFieldType)).required('Field type is required'),
  inactive: yup.boolean().required(),
});

export const EditCustomField = ({ customField, onSave, onCancel }: EditCustomFieldProps): JSX.Element => {
  const isEditMode = !!customField;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CustomFieldFormValues>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      name: customField?.name ?? '',
      dataType: customField?.dataType ?? CustomFieldDataType.String,
      fieldType: customField?.fieldType ?? CustomFieldType.None,
      inactive: customField?.inactive ?? false,
    },
    mode: 'onSubmit',
  });

  const [options, setOptions] = useState<CustomFieldOption[]>(customField?.options ?? []);

  const addOption = () => {
    setOptions([...options, { name: '', value: '' }]);
  };

  const updateOption = (index: number, field: 'name' | 'value', val: string) => {
    const updated = [...options];
    updated[index] = { ...updated[index], [field]: val };
    setOptions(updated);
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const onSubmit = (values: CustomFieldFormValues) => {
    const payload: CreateOrUpdateCustomFieldViewModel = {
      customFieldId: customField?.customFieldId ?? null,
      name: values.name,
      dataType: values.dataType,
      fieldType: values.fieldType,
      inactive: values.inactive,
      options: options.length > 0 ? options.filter((o) => o.name || o.value) : null,
    };

    onSave(payload);
  };

  return (
    <div className="surface-section p-4 surface-border border-round mb-3 w-full relative">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled grid">
        <div className="col-12 field mb-0 p-0">
          <FormInputText
            control={control}
            errors={errors}
            field="name"
            label="Name"
            placeholder="e.g. loanInformation.houseColor"
            required={true}
          />
        </div>

        <div className="col-12 md:col-6 field mb-0 p-0 md:pr-2">
          <FormDropdown
            control={control}
            errors={errors}
            field="dataType"
            label="Data Type"
            options={DATA_TYPE_OPTIONS}
            placeholder="Select data type"
            required={true}
          />
        </div>

        <div className="col-12 md:col-6 field mb-0 p-0 md:pl-2">
          <FormDropdown
            control={control}
            errors={errors}
            field="fieldType"
            label="Field Type"
            options={FIELD_TYPE_OPTIONS}
            placeholder="Select field type"
            required={true}
          />
        </div>

        <div className="col-12 field mb-3 p-0">
          <FormCheckbox control={control} errors={errors} field="inactive" label="Inactive" />
        </div>

        <div className="col-12 field mb-3 p-0">
          <label className="block mb-1 font-medium">Options</label>
          {options.length > 0 && (
            <div className="flex flex-column gap-2">
              {options.map((opt, index) => (
                <div key={index} className="flex align-items-center gap-2">
                  <div className="flex-1">
                    <InputText
                      value={opt.name ?? ''}
                      onChange={(e) => updateOption(index, 'name', e.target.value)}
                      placeholder="Option name"
                      className="w-full"
                    />
                  </div>
                  <div className="flex-1">
                    <InputText
                      value={opt.value ?? ''}
                      onChange={(e) => updateOption(index, 'value', e.target.value)}
                      placeholder="Option value"
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => removeOption(index)}
                    style={{ minWidth: '32px', padding: '0.25rem' }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="mt-2">
            <Button
              type="button"
              label="Add Option"
              icon="pi pi-plus"
              link
              onClick={addOption}
              style={{ padding: '0.5rem', width: 'auto' }}
            />
          </div>
        </div>

        <div className="col-12 flex flex-column sm:flex-row justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined w-full sm:w-auto"
            style={{ minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button w-full sm:w-auto"
            style={{ minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
